import { FormEvent, useEffect, useMemo, useState } from "react";
import type { Session } from "@supabase/supabase-js";
import { isConfigured, supabase } from "./lib/supabase";
import { maskId } from "./lib/parsers";
import {
  attendanceCheckProblems,
  exportAttendanceCheckXlsx,
} from "./lib/attendanceCheck";
import { downloadHuayueAttendanceXlsx } from "./lib/huayueAttendance";
import {
  calculateExpenseByMonth,
  calculateProjectExpense,
  calculateWageDetails,
} from "./lib/wageExpense";
import {
  clearQueuedAttendance,
  onAttendanceQueueChange,
  queueAttendance,
  queuedAttendance,
  removeQueuedAttendance,
  sendAttendancePayload,
  updateQueuedAttendance,
} from "./lib/offlineQueue";
import type { AttendanceSyncPayload } from "./lib/offlineQueue";
import { AiAssistantView } from "./components/AiAssistantView";
import type {
  Attendance,
  Person,
  Project,
} from "./types";

type View =
  | "attendance"
  | "ai"
  | "people"
  | "reports"
  | "projects";
type InstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
};
const now = new Date();
const APP_VERSION = "2026.07.30.1";
const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;

function errText(error: unknown) {
  const value = error as { message?: string; code?: string } | null;
  if (value?.code === "23505") return "同一人员当天已有考勤，不能保存重复记录";
  return value?.message || "操作失败";
}

function mergeProjectMembersWithProfiles(
  members: Record<string, unknown>[],
  profiles: Record<string, unknown>[],
): Person[] {
  const profilesById = new Map(
    profiles.map((profile) => [String(profile.id || ""), profile]),
  );
  return members.map((member) => {
    const profile = profilesById.get(String(member.profile_id || ""));
    return {
      ...member,
      ...(profile || {}),
      id: String(member.id || ""),
      project_id: String(member.project_id || ""),
      profile_id: String(member.profile_id || profile?.id || ""),
      active: Boolean(member.active) && (profile ? Boolean(profile.active) : true),
    } as Person;
  });
}

export function App() {
  const [session, setSession] = useState<Session | null>(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setChecking(false);
    });
    const { data } = supabase.auth.onAuthStateChange((_event, next) =>
      setSession(next),
    );
    return () => data.subscription.unsubscribe();
  }, []);

  if (!isConfigured) return <SetupRequired />;
  if (checking) return <Splash text="正在连接云端数据库…" />;
  if (!session) return <Login />;
  return <Dashboard session={session} />;
}

function Splash({ text }: { text: string }) {
  return (
    <div className="center-page">
      <div className="splash-mark">时</div>
      <p>{text}</p>
    </div>
  );
}

function SetupRequired() {
  return (
    <div className="center-page">
      <div className="setup-card">
        <div className="splash-mark">时</div>
        <h1>等待连接 Supabase</h1>
        <p>
          网站代码已经准备好。请复制 <code>.env.example</code> 为{" "}
          <code>.env.local</code>，填写项目地址和 Publishable Key。
        </p>
        <div className="setup-lines">
          <span>VITE_SUPABASE_URL</span>
          <span>VITE_SUPABASE_PUBLISHABLE_KEY</span>
        </div>
        <small>不要把 Secret Key 或 service_role Key 放入前端环境变量。</small>
      </div>
    </div>
  );
}

function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [mode, setMode] = useState<"login" | "register">("login");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setMessage("");
    const result =
      mode === "login"
        ? await supabase.auth.signInWithPassword({ email, password })
        : await supabase.auth.signUp({ email, password });
    setBusy(false);
    if (result.error) setMessage(errText(result.error));
    else if (mode === "register" && !result.data.session)
      setMessage("注册成功，请到邮箱完成验证后登录。 ");
  };

  return (
    <div className="login-page">
      <div className="login-brand">
        <div className="brand-mark">时</div>
        <div>
          <b>工时记</b>
          <span>Cloudflare Pages＋Supabase</span>
        </div>
      </div>
      <form className="login-card" onSubmit={submit}>
        <p className="eyebrow">ADMIN PORTAL</p>
        <h1>{mode === "login" ? "管理员登录" : "创建管理员账号"}</h1>
        <p className="muted">项目、考勤和工资数据将在手机与桌面端同步。</p>
        {message && <div className="notice">{message}</div>}
        <label>
          邮箱
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="name@example.com"
          />
        </label>
        <label>
          密码
          <input
            type="password"
            required
            minLength={6}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="至少 6 位"
          />
        </label>
        <button className="primary wide" disabled={busy}>
          {busy ? "请稍候…" : mode === "login" ? "登录" : "注册"}
        </button>
        <button
          type="button"
          className="text-btn"
          onClick={() => {
            setMode(mode === "login" ? "register" : "login");
            setMessage("");
          }}
        >
          {mode === "login" ? "首次使用？创建账号" : "已有账号？返回登录"}
        </button>
      </form>
    </div>
  );
}

function Dashboard({ session }: { session: Session }) {
  const [view, setView] = useState<View>("attendance");
  const [projects, setProjects] = useState<Project[]>([]);
  const [projectId, setProjectId] = useState("");
  const [people, setPeople] = useState<Person[]>([]);
  const [attendance, setAttendance] = useState<Attendance[]>([]);
  const [projectAttendance, setProjectAttendance] = useState<Attendance[]>([]);
  const [projectPeople, setProjectPeople] = useState<Person[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [date, setDate] = useState(today);
  const [loading, setLoading] = useState(true);
  const [notice, setNotice] = useState<{ text: string; bad?: boolean } | null>(
    null,
  );
  const [installPrompt, setInstallPrompt] =
    useState<InstallPromptEvent | null>(null);
  const [standalone, setStandalone] = useState(
    window.matchMedia("(display-mode: standalone)").matches ||
      Boolean((navigator as Navigator & { standalone?: boolean }).standalone),
  );
  const [online, setOnline] = useState(navigator.onLine);
  const [queueCount, setQueueCount] = useState(
    queuedAttendance(session.user.id).length,
  );
  const [queueSyncing, setQueueSyncing] = useState(false);
  const month = date.slice(0, 7);

  useEffect(() => {
    const captureInstall = (event: Event) => {
      event.preventDefault();
      setInstallPrompt(event as InstallPromptEvent);
    };
    const installed = () => {
      setInstallPrompt(null);
      setStandalone(true);
    };
    window.addEventListener("beforeinstallprompt", captureInstall);
    window.addEventListener("appinstalled", installed);
    return () => {
      window.removeEventListener("beforeinstallprompt", captureInstall);
      window.removeEventListener("appinstalled", installed);
    };
  }, []);

  const installApp = async () => {
    if (installPrompt) {
      await installPrompt.prompt();
      const choice = await installPrompt.userChoice;
      if (choice.outcome === "accepted") setStandalone(true);
      setInstallPrompt(null);
      return;
    }
    const isAppleMobile = /iphone|ipad|ipod/i.test(navigator.userAgent);
    setNotice({
      text: isAppleMobile
        ? "iPhone 请使用 Safari 打开本页，点击“分享”，再选择“添加到主屏幕”"
        : "请打开浏览器菜单，选择“安装应用”或“添加到主屏幕”",
    });
  };

  const loadProjects = async () => {
    const { data, error } = await supabase
      .from("projects")
      .select("*")
      .eq("active", true)
      .order("created_at");
    if (error) return setNotice({ text: errText(error), bad: true });
    setProjects((data || []) as Project[]);
    if (!projectId && data?.[0]) setProjectId(data[0].id);
    setLoading(false);
  };
  const loadData = async () => {
    if (!projectId) {
      setPeople([]);
      setAttendance([]);
      return;
    }
    setLoading(true);
    const start = `${month}-01`;
    const endDate = new Date(
      Number(month.slice(0, 4)),
      Number(month.slice(5, 7)),
      0,
    );
    const end = `${month}-${String(endDate.getDate()).padStart(2, "0")}`;
    const [peopleRes, profilesRes, attRes] = await Promise.all([
      supabase
        .from("people")
        .select("*")
        .eq("project_id", projectId)
        .eq("active", true)
        .order("created_at"),
      supabase
        .from("person_profiles")
        .select("*")
        .eq("active", true)
        .order("name"),
      supabase
        .from("attendance")
        .select("*")
        .eq("project_id", projectId)
        .gte("work_date", start)
        .lte("work_date", end)
        .order("work_date", { ascending: false }),
    ]);
    if (peopleRes.error || profilesRes.error || attRes.error)
      setNotice({ text: errText(peopleRes.error || profilesRes.error || attRes.error), bad: true });
    setPeople(mergeProjectMembersWithProfiles(
      (peopleRes.data || []) as Record<string, unknown>[],
      (profilesRes.data || []) as Record<string, unknown>[],
    ));
    setAttendance((attRes.data || []) as Attendance[]);
    setLoading(false);
  };
  useEffect(() => {
    void loadProjects();
  }, []);
  useEffect(() => {
    void loadData();
  }, [projectId, month]);
  useEffect(() => {
    if (view !== "reports") return;
    if (!projectId) {
      setProjectAttendance([]);
      setProjectPeople([]);
      setHistoryLoading(false);
      return;
    }
    let cancelled = false;
    const loadProjectHistory = async () => {
      setHistoryLoading(true);
      const [peopleRes, profilesRes] = await Promise.all([
        supabase
          .from("people")
          .select("*")
          .eq("project_id", projectId)
          .order("created_at"),
        supabase
          .from("person_profiles")
          .select("*")
          .order("name"),
      ]);
      if (peopleRes.error || profilesRes.error) {
        if (!cancelled) {
          setNotice({ text: errText(peopleRes.error || profilesRes.error), bad: true });
          setHistoryLoading(false);
        }
        return;
      }

      const allRows: Attendance[] = [];
      const pageSize = 1000;
      for (let from = 0; ; from += pageSize) {
        const rowsRes = await supabase
          .from("attendance")
          .select("*")
          .eq("project_id", projectId)
          .order("work_date")
          .range(from, from + pageSize - 1);
        if (rowsRes.error) {
          if (!cancelled)
            setNotice({ text: errText(rowsRes.error), bad: true });
          break;
        }
        const page = (rowsRes.data || []) as Attendance[];
        allRows.push(...page);
        if (page.length < pageSize) break;
      }
      if (!cancelled) {
        setProjectPeople(mergeProjectMembersWithProfiles(
          (peopleRes.data || []) as Record<string, unknown>[],
          (profilesRes.data || []) as Record<string, unknown>[],
        ));
        setProjectAttendance(allRows);
        setHistoryLoading(false);
      }
    };
    void loadProjectHistory();
    return () => {
      cancelled = true;
    };
  }, [view, projectId]);

  const project = projects.find((p) => p.id === projectId);
  const summary = useMemo(
    () =>
      people.flatMap((person) => {
        const rows = attendance.filter((a) => a.person_id === person.id);
        if (!rows.length) return [];
        const regular = rows.reduce((s, r) => s + Number(r.regular_hours), 0);
        const overtime = rows.reduce((s, r) => s + Number(r.overtime_hours), 0);
        const days = regular / 8;
        const base =
          person.salary_type === "月薪"
            ? rows.length
              ? Number(person.rate)
              : 0
            : days * Number(person.rate);
        return [
          {
            ...person,
            regular,
            overtime,
            days,
            payable: base + overtime * Number(person.overtime_rate),
          },
        ];
      }),
    [people, attendance],
  );

  const notify = (text: string, bad = false) => {
    setNotice({ text, bad });
    window.setTimeout(() => setNotice(null), 5000);
  };
  const syncQueuedAttendance = async (showResult = true) => {
    if (queueSyncing) return;
    const queued = queuedAttendance(session.user.id);
    setQueueCount(queued.length);
    if (!queued.length) {
      if (showResult) notify("当前没有待同步考勤");
      return;
    }
    if (!navigator.onLine) {
      if (showResult) notify("当前处于离线状态，联网后将自动同步", true);
      return;
    }
    setQueueSyncing(true);
    let completed = 0;
    let failedMessage = "";
    const { data: sessionData } = await supabase.auth.getSession();
    const token = sessionData.session?.access_token;
    if (!token) {
      setQueueSyncing(false);
      notify("登录已过期，重新登录后会继续同步", true);
      return;
    }
    for (const item of queued) {
      try {
        await sendAttendancePayload(item.payload, token);
        removeQueuedAttendance(item.id);
        completed += item.payload.attendance.length;
      } catch (error) {
        failedMessage = errText(error);
        updateQueuedAttendance(item.id, {
          attempts: item.attempts + 1,
          lastError: failedMessage,
        });
        break;
      }
    }
    setQueueSyncing(false);
    const remaining = queuedAttendance(session.user.id).length;
    setQueueCount(remaining);
    if (completed) await loadData();
    if (showResult || completed || failedMessage) {
      if (failedMessage)
        notify(`已同步 ${completed} 条，仍有 ${remaining} 批待处理：${failedMessage}`, true);
      else notify(`离线考勤已同步 ${completed} 条`);
    }
  };
  useEffect(() => {
    const refresh = () => setQueueCount(queuedAttendance(session.user.id).length);
    const becameOnline = () => {
      setOnline(true);
      void syncQueuedAttendance(false);
    };
    const becameOffline = () => setOnline(false);
    const unsubscribe = onAttendanceQueueChange(refresh);
    window.addEventListener("online", becameOnline);
    window.addEventListener("offline", becameOffline);
    refresh();
    if (navigator.onLine) void syncQueuedAttendance(false);
    return () => {
      unsubscribe();
      window.removeEventListener("online", becameOnline);
      window.removeEventListener("offline", becameOffline);
    };
  }, [session.user.id]);
  const navItems: [View, string, string][] = [
    ["attendance", "▦", "项目考勤"],
    ["ai", "✦", "AI助手"],
    ["people", "◎", "人员管理"],
    ["reports", "↗", "月度报表"],
    ["projects", "◇", "项目管理"],
  ];

  return (
    <div className="shell">
      <aside>
        <div className="brand">
          <div className="brand-mark">时</div>
          <div>
            <b>工时记</b>
            <span>云端考勤管理</span>
          </div>
        </div>
        <nav>
          {navItems.map(([id, icon, label]) => (
            <button
              key={id}
              className={view === id ? "active" : ""}
              onClick={() => setView(id)}
            >
              <i>{icon}</i>
              {label}
            </button>
          ))}
        </nav>
        <div className="account">
          <span>{session.user.email}</span>
          <button onClick={() => supabase.auth.signOut()}>退出登录</button>
        </div>
      </aside>
      <main>
        <header>
          <div className="page-heading">
            <p className="eyebrow">CLOUD ATTENDANCE</p>
            <h1>{navItems.find((x) => x[0] === view)?.[2]}</h1>
            <label className="project-select">
              当前项目
              <select
                value={projectId}
                onChange={(e) => setProjectId(e.target.value)}
              >
                <option value="">请选择项目</option>
                {projects.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </header>
        {notice && (
          <div className={`notice ${notice.bad ? "bad" : ""}`}>
            {notice.text}
          </div>
        )}

        {view === "attendance" && (
          <AttendanceView
            projectId={projectId}
            date={date}
            setDate={setDate}
            people={people}
            attendance={attendance}
            loading={loading}
            reload={loadData}
            notify={notify}
          />
        )}
        {view === "ai" && (
          <AiAssistantView
            project={project}
            date={date}
            setDate={setDate}
            people={people}
            attendance={attendance}
            summary={summary}
            reload={loadData}
            notify={notify}
          />
        )}
        {view === "people" && (
          <PeopleView
            projectId={projectId}
            people={people}
            summary={summary}
            month={date.slice(0, 7)}
            reload={loadData}
            notify={notify}
          />
        )}
        {view === "reports" && (
          <ReportsView
            project={project}
            date={date}
            setDate={setDate}
            attendance={attendance}
            people={people}
            projectAttendance={projectAttendance}
            projectPeople={projectPeople}
            historyLoading={historyLoading}
            notify={notify}
          />
        )}
        {view === "projects" && (
          <ProjectsView
            ownerId={session.user.id}
            projects={projects}
            current={projectId}
            select={setProjectId}
            reload={loadProjects}
            notify={notify}
          />
        )}
      </main>
      <div className="bottom-nav">
        {navItems.map(([id, icon, label]) => (
          <button
            key={id}
            className={view === id ? "active" : ""}
            onClick={() => setView(id)}
          >
            <i>{icon}</i>
            <span>{label.slice(0, 2)}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

type CommonProps = {
  projectId: string;
  people: Person[];
  reload: () => Promise<void>;
  notify: (text: string, bad?: boolean) => void;
};

type AttendanceDetailFilter = "all" | "date" | "name" | "area";

function AttendanceView({
  projectId,
  people,
  date,
  setDate,
  attendance,
  loading,
  reload,
  notify,
}: CommonProps & {
  date: string;
  setDate: (v: string) => void;
  attendance: Attendance[];
  loading: boolean;
}) {
  const [editSaving, setEditSaving] = useState(false);
  const [detailFilter, setDetailFilter] = useState<AttendanceDetailFilter>("all");
  const [detailDate, setDetailDate] = useState(date);
  const [detailName, setDetailName] = useState("");
  const [detailArea, setDetailArea] = useState("");
  const [appliedAttendanceFilter, setAppliedAttendanceFilter] = useState<{
    type: AttendanceDetailFilter;
    date: string;
    name: string;
    area: string;
  } | null>(null);
  const [editForm, setEditForm] = useState({
    id: "",
    person_id: "",
    work_date: "",
    area: "",
    regular_hours: "",
    overtime_hours: "",
    remark: "",
  });
  const personById = useMemo(
    () => new Map(people.map((person) => [person.id, person])),
    [people],
  );
  const attendancePeople = useMemo(
    () =>
      Array.from(new Set(attendance.map((row) => row.person_id)))
        .map((id) => personById.get(id))
        .filter((person): person is Person => Boolean(person))
        .sort((left, right) => left.name.localeCompare(right.name, "zh-CN")),
    [attendance, personById],
  );
  const attendanceAreas = useMemo(
    () =>
      Array.from(
        new Set(attendance.map((row) => row.area.trim()).filter(Boolean)),
      ).sort((left, right) => left.localeCompare(right, "zh-CN")),
    [attendance],
  );
  useEffect(() => {
    setAppliedAttendanceFilter(null);
    setDetailDate(date);
    setDetailName("");
    setDetailArea("");
    setEditForm({ id: "", person_id: "", work_date: "", area: "", regular_hours: "", overtime_hours: "", remark: "" });
  }, [projectId, date.slice(0, 7)]);
  const filteredAttendance = useMemo(
    () => {
      if (!appliedAttendanceFilter) return [];
      return attendance.filter((row) => {
        if (appliedAttendanceFilter.type === "date") return row.work_date === appliedAttendanceFilter.date;
        if (appliedAttendanceFilter.type === "name") {
          const keyword = appliedAttendanceFilter.name.replace(/\s/g, "").trim();
          const personName = personById.get(row.person_id)?.name || "";
          return !keyword || personName.includes(keyword);
        }
        if (appliedAttendanceFilter.type === "area")
          return !appliedAttendanceFilter.area || row.area === appliedAttendanceFilter.area;
        return true;
      });
    },
    [attendance, appliedAttendanceFilter, personById],
  );
  const searchAttendance = () => {
    if (detailFilter === "name" && !detailName.trim()) return notify("请输入或选择姓名", true);
    if (detailFilter === "area" && !detailArea) return notify("请选择区域", true);
    setAppliedAttendanceFilter({ type: detailFilter, date: detailDate, name: detailName, area: detailArea });
    setEditForm({ id: "", person_id: "", work_date: "", area: "", regular_hours: "", overtime_hours: "", remark: "" });
  };
  const clearAttendanceSearch = () => {
    setAppliedAttendanceFilter(null);
    setDetailName("");
    setDetailArea("");
    setEditForm({ id: "", person_id: "", work_date: "", area: "", regular_hours: "", overtime_hours: "", remark: "" });
  };

  const beginEdit = (row: Attendance) =>
    setEditForm({
      id: row.id,
      person_id: row.person_id,
      work_date: row.work_date,
      area: row.area || "",
      regular_hours: String(row.regular_hours),
      overtime_hours: String(row.overtime_hours),
      remark: row.remark || "",
    });
  const saveEdit = async () => {
    const regular = Number(editForm.regular_hours);
    const overtime = Number(editForm.overtime_hours);
    if (!editForm.person_id || !editForm.work_date)
      return notify("姓名和日期不能为空", true);
    if (!Number.isFinite(regular) || regular < 0 || regular > 8)
      return notify("正班小时必须在0～8小时之间", true);
    if (!Number.isFinite(overtime) || overtime < 0 || overtime > 24)
      return notify("加班小时必须在0～24小时之间", true);
    const area = editForm.area.trim();
    const sameArea = attendance.find(row =>
      row.id !== editForm.id &&
      row.person_id === editForm.person_id &&
      row.work_date === editForm.work_date &&
      String(row.area || "").trim() === area
    );
    if (sameArea) return notify("该人员当天已经存在相同区域的考勤，请直接修改原区域记录", true);
    const dailyRegular = attendance
      .filter(row =>
        row.id !== editForm.id &&
        row.person_id === editForm.person_id &&
        row.work_date === editForm.work_date
      )
      .reduce((sum, row) => sum + Number(row.regular_hours || 0), regular);
    if (dailyRegular > 8.0001)
      return notify(`该人员当天各区域正班合计 ${dailyRegular} 小时，超过8小时；加班不计入`, true);
    setEditSaving(true);
    const { error } = await supabase
      .from("attendance")
      .update({
        person_id: editForm.person_id,
        work_date: editForm.work_date,
        area,
        regular_hours: regular,
        overtime_hours: overtime,
        remark: editForm.remark.trim(),
      })
      .eq("id", editForm.id)
      .eq("project_id", projectId);
    setEditSaving(false);
    if (error) notify(errText(error), true);
    else {
      setEditForm({
        id: "",
        person_id: "",
        work_date: "",
        area: "",
        regular_hours: "",
        overtime_hours: "",
        remark: "",
      });
      notify("考勤修改已保存");
      await reload();
    }
  };
  const remove = async (id: string) => {
    if (!window.confirm("确定删除这条考勤吗？")) return;
    const { error } = await supabase.from("attendance").delete().eq("id", id);
    if (error) notify(errText(error), true);
    else {
      if (editForm.id === id)
        setEditForm({
          id: "",
          person_id: "",
          work_date: "",
          area: "",
          regular_hours: "",
          overtime_hours: "",
          remark: "",
        });
      await reload();
    }
  };
  const totalHours = attendance.reduce(
    (s, x) => s + Number(x.regular_hours),
    0,
  );
  const totalOvertime = attendance.reduce(
    (s, x) => s + Number(x.overtime_hours),
    0,
  );
  const participantCount = new Set(
    attendance
      .filter(
        (row) =>
          row.project_id === projectId &&
          row.work_date.startsWith(date.slice(0, 7)),
      )
      .map((row) => row.person_id),
  ).size;
  const editPanel = editForm.id ? (
    <div className="edit-attendance-form">
      <label>
        人员
        <select
          value={editForm.person_id}
          onChange={(e) =>
            setEditForm({ ...editForm, person_id: e.target.value })
          }
        >
          {people.map((person) => (
            <option key={person.id} value={person.id}>
              {person.name}
            </option>
          ))}
        </select>
      </label>
      <label>
        日期
        <input
          type="date"
          value={editForm.work_date}
          onChange={(e) =>
            setEditForm({ ...editForm, work_date: e.target.value })
          }
        />
      </label>
      <label>
        区域
        <input
          value={editForm.area}
          onChange={(e) => setEditForm({ ...editForm, area: e.target.value })}
        />
      </label>
      <label>
        正班小时
        <input
          type="number"
          min="0"
          max="8"
          step="0.5"
          value={editForm.regular_hours}
          onChange={(e) =>
            setEditForm({ ...editForm, regular_hours: e.target.value })
          }
        />
      </label>
      <label>
        加班小时
        <input
          type="number"
          min="0"
          max="24"
          step="0.5"
          value={editForm.overtime_hours}
          onChange={(e) =>
            setEditForm({ ...editForm, overtime_hours: e.target.value })
          }
        />
      </label>
      <label>
        备注
        <input
          value={editForm.remark}
          onChange={(e) => setEditForm({ ...editForm, remark: e.target.value })}
        />
      </label>
      <div className="actions">
        <button
          className="secondary"
          onClick={() =>
            setEditForm({
              id: "",
              person_id: "",
              work_date: "",
              area: "",
              regular_hours: "",
              overtime_hours: "",
              remark: "",
            })
          }
        >
          取消
        </button>
        <button
          className="primary"
          disabled={editSaving}
          onClick={() => void saveEdit()}
        >
          {editSaving ? "保存中…" : "保存修改"}
        </button>
      </div>
    </div>
  ) : null;

  return (
    <>
      <div className="stats">
        <Stat label="本月参勤人员" value={participantCount} unit="人" />
        <Stat label="本月正班" value={totalHours} unit="小时" />
        <Stat label="本月加班" value={totalOvertime} unit="小时" />
        <Stat label="已录明细" value={attendance.length} unit="条" />
      </div>
      <section className="card history">
        <div className="card-head">
          <div>
            <h2>本月考勤明细</h2>
            <p>支持全览、按时间、姓名或区域筛选；可修改或删除错误记录</p>
          </div>
          <span className="pill">{appliedAttendanceFilter ? `${filteredAttendance.length} 条` : "等待查询"}</span>
        </div>
        <div className="attendance-detail-filters">
          <div className="attendance-filter-tabs">
            {(
              [
                ["all", "全览"],
                ["date", "时间"],
                ["name", "姓名"],
                ["area", "区域"],
              ] as Array<[AttendanceDetailFilter, string]>
            ).map(([value, label]) => (
              <button
                type="button"
                key={value}
                className={value === "all" ? appliedAttendanceFilter?.type === "all" ? "active" : "" : detailFilter === value ? "active" : ""}
                onClick={() => {
                  setDetailFilter(value);
                  setEditForm({ id: "", person_id: "", work_date: "", area: "", regular_hours: "", overtime_hours: "", remark: "" });
                  if (value === "all") {
                    setAppliedAttendanceFilter((current) =>
                      current?.type === "all"
                        ? null
                        : { type: "all", date: detailDate, name: "", area: "" },
                    );
                    return;
                  }
                  setAppliedAttendanceFilter(null);
                  if (value === "date" && !detailDate) setDetailDate(date);
                  if (value === "area" && !detailArea)
                    setDetailArea(attendanceAreas[0] || "");
                }}
              >
                {label}
              </button>
            ))}
          </div>
          {detailFilter === "date" && (
            <label>选择日期<input type="date" value={detailDate} onChange={(event) => setDetailDate(event.target.value)} /></label>
          )}
          {detailFilter === "name" && (
            <label>输入或点选姓名<input list="attendance-name-options" value={detailName} onChange={(event) => setDetailName(event.target.value)} placeholder="输入部分姓名即可筛选" /><datalist id="attendance-name-options">{attendancePeople.map((person) => <option key={person.id} value={person.name} />)}</datalist></label>
          )}
          {detailFilter === "area" && (
            <label>选择区域<select value={detailArea} onChange={(event) => setDetailArea(event.target.value)}>{attendanceAreas.map((areaName) => <option key={areaName} value={areaName}>{areaName}</option>)}</select></label>
          )}
          <div className="attendance-query-actions"><button className="primary" type="button" onClick={searchAttendance}>查询</button><button className="secondary" type="button" onClick={clearAttendanceSearch}>清除</button></div>
        </div>
        {appliedAttendanceFilter ? editPanel : null}
        {!appliedAttendanceFilter ? (
          <div className="people-query-empty"><b>考勤明细默认隐藏</b><span>请选择全览、时间、姓名或区域，然后点击“查询”</span></div>
        ) : <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>日期</th>
                <th>姓名</th>
                <th>区域</th>
                <th>正班</th>
                <th>加班</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6}>正在读取…</td>
                </tr>
              ) : filteredAttendance.length ? (
                filteredAttendance.map((row) => (
                  <tr key={row.id}>
                    <td>{row.work_date}</td>
                    <td>
                      {people.find((p) => p.id === row.person_id)?.name || "—"}
                    </td>
                    <td>{row.area || "—"}</td>
                    <td>{row.regular_hours}h</td>
                    <td>{row.overtime_hours}h</td>
                    <td>
                      <div className="row-actions">
                        <button
                          className="text-edit"
                          onClick={() => beginEdit(row)}
                        >
                          修改
                        </button>
                        <button
                          className="danger"
                          onClick={() => void remove(row.id)}
                        >
                          删除
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr><td colSpan={6}>当前筛选条件下没有考勤记录</td></tr>
              )}
            </tbody>
          </table>
        </div>}
      </section>
    </>
  );
}

function PeopleView({
  projectId,
  people,
  summary,
  month,
  reload,
  notify,
}: {
  projectId: string;
  people: Person[];
  summary: Array<Person & { regular: number; overtime: number; days: number; payable: number }>;
  month: string;
  reload: () => Promise<void>;
  notify: (text: string, bad?: boolean) => void;
}) {
  const [keyword, setKeyword] = useState("");
  const [tradeFilter, setTradeFilter] = useState("");
  const [appliedFilter, setAppliedFilter] = useState<{ keyword: string; trade: string } | null>(null);
  const [editingPerson, setEditingPerson] = useState<Person | null>(null);
  const [personSaving, setPersonSaving] = useState(false);
  const [revealedFields, setRevealedFields] = useState<Set<string>>(() => new Set());
  useEffect(() => {
    setKeyword("");
    setTradeFilter("");
    setAppliedFilter(null);
    setEditingPerson(null);
    setRevealedFields(new Set());
  }, [projectId]);
  const toggleSensitiveField = (key: string) =>
    setRevealedFields((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  const summaryById = useMemo(
    () => new Map(summary.map((person) => [person.id, person])),
    [summary],
  );
  const trades = useMemo(
    () => Array.from(new Set(people.map((person) => person.trade.trim()).filter(Boolean))).sort((a, b) => a.localeCompare(b, "zh-CN")),
    [people],
  );
  const filteredPeople = useMemo(() => {
    if (!appliedFilter) return [];
    const query = appliedFilter.keyword.replace(/\s/g, "").trim().toLowerCase();
    return people.filter((person) => {
      const matchesKeyword = !query || [person.name, person.trade, person.phone, person.id_no, person.bank_card, person.bank_name]
        .some((value) => String(value || "").replace(/\s/g, "").toLowerCase().includes(query));
      return matchesKeyword && (!appliedFilter.trade || person.trade === appliedFilter.trade);
    });
  }, [people, appliedFilter]);
  const searchPeople = () => {
    if (!keyword.trim() && !tradeFilter)
      return notify("请输入查询内容或选择工种", true);
    setAppliedFilter({ keyword, trade: tradeFilter });
    setEditingPerson(null);
    setRevealedFields(new Set());
  };
  const clearPeopleSearch = () => {
    setKeyword("");
    setTradeFilter("");
    setAppliedFilter(null);
    setEditingPerson(null);
    setRevealedFields(new Set());
  };
  const beginPersonEdit = (person: Person) => {
    setEditingPerson({ ...person });
    window.requestAnimationFrame(() =>
      document.querySelector(".person-edit-form")?.scrollIntoView({ behavior: "smooth", block: "start" }),
    );
  };
  const savePersonEdit = async () => {
    if (!editingPerson || !projectId) return;
    const name = editingPerson.name.replace(/\s/g, "").trim();
    if (!name) return notify("姓名不能为空", true);
    if (people.some((person) => person.id !== editingPerson.id && person.name === name))
      return notify(`姓名“${name}”已存在，不能保存重复人员`, true);
    if (!window.confirm(`确认保存“${name}”的人员信息和薪资标准吗？`)) return;
    if (!editingPerson.profile_id)
      return notify("该人员缺少全局主档关联，请先重新登录或执行数据库迁移", true);
    setPersonSaving(true);
    const { data: saved, error } = await supabase
      .from("person_profiles")
      .update({
        canonical_name: name,
        name,
        trade: editingPerson.trade.trim(),
        phone: editingPerson.phone.trim(),
        id_no: editingPerson.id_no.replace(/\s/g, "").toUpperCase(),
        bank_card: editingPerson.bank_card.replace(/\s/g, ""),
        bank_name: editingPerson.bank_name.trim(),
        salary_type: editingPerson.salary_type,
        rate: Math.max(0, Number(editingPerson.rate) || 0),
        overtime_rate: Math.max(0, Number(editingPerson.overtime_rate) || 0),
        remark: editingPerson.remark.trim(),
      })
      .eq("id", editingPerson.profile_id)
      .select("id");
    setPersonSaving(false);
    if (error)
      return notify(error.code === "23505" ? `姓名“${name}”已存在，不能保存重复人员` : errText(error), true);
    if (!saved?.length) return notify("没有找到可修改的全局人员主档，请刷新后重试", true);
    setEditingPerson(null);
    await reload();
    notify(`“${name}”的人员信息和薪资已更新`);
  };
  const totalPayable = summary.reduce((sum, person) => sum + person.payable, 0);
  return (
    <section className="card people-management">
      <div className="card-head">
        <div><h2>人员信息与薪资</h2><p>{month} · 共 {people.length} 人，当月参勤 {summary.length} 人</p></div>
        <span className="pill">本月合计 ¥{totalPayable.toFixed(2)}</span>
      </div>
      <div className="people-filters">
        <label>搜索人员<input value={keyword} onChange={(event) => setKeyword(event.target.value)} onKeyDown={(event) => { if (event.key === "Enter") searchPeople(); }} placeholder="姓名、工种、手机号、证件或银行卡" /></label>
        <label>工种筛选<select value={tradeFilter} onChange={(event) => setTradeFilter(event.target.value)}><option value="">全部工种</option>{trades.map((trade) => <option key={trade} value={trade}>{trade}</option>)}</select></label>
        <div className="people-filter-actions"><button className="primary" type="button" onClick={searchPeople}>查询</button><button className="secondary" type="button" onClick={clearPeopleSearch}>清除</button></div>
        <span className="pill">{appliedFilter ? `显示 ${filteredPeople.length} 人` : "等待查询"}</span>
      </div>
      {editingPerson && (
        <div className="person-edit-form">
          <label>姓名<input value={editingPerson.name} onChange={(event) => setEditingPerson({ ...editingPerson, name: event.target.value })} /></label>
          <label>工种<input value={editingPerson.trade} onChange={(event) => setEditingPerson({ ...editingPerson, trade: event.target.value })} /></label>
          <label>手机号<input value={editingPerson.phone} onChange={(event) => setEditingPerson({ ...editingPerson, phone: event.target.value })} /></label>
          <label>身份证号<input value={editingPerson.id_no} onChange={(event) => setEditingPerson({ ...editingPerson, id_no: event.target.value })} /></label>
          <label>银行卡号<input value={editingPerson.bank_card} onChange={(event) => setEditingPerson({ ...editingPerson, bank_card: event.target.value })} /></label>
          <label>开户行<input value={editingPerson.bank_name} onChange={(event) => setEditingPerson({ ...editingPerson, bank_name: event.target.value })} /></label>
          <label>薪资类型<select value={editingPerson.salary_type} onChange={(event) => setEditingPerson({ ...editingPerson, salary_type: event.target.value as "日薪" | "月薪" })}><option value="日薪">日薪</option><option value="月薪">月薪</option></select></label>
          <label>工资单价<input type="number" min="0" step="0.01" value={editingPerson.rate} onChange={(event) => setEditingPerson({ ...editingPerson, rate: Number(event.target.value) })} /></label>
          <label>加班单价/小时<input type="number" min="0" step="0.01" value={editingPerson.overtime_rate} onChange={(event) => setEditingPerson({ ...editingPerson, overtime_rate: Number(event.target.value) })} /></label>
          <label>备注<input value={editingPerson.remark} onChange={(event) => setEditingPerson({ ...editingPerson, remark: event.target.value })} /></label>
          <div className="actions"><button className="secondary" type="button" onClick={() => setEditingPerson(null)}>取消</button><button className="primary" type="button" disabled={personSaving} onClick={() => void savePersonEdit()}>{personSaving ? "保存中…" : "保存修改"}</button></div>
        </div>
      )}
      {!appliedFilter ? (
        <div className="people-query-empty"><b>人员信息默认隐藏</b><span>请输入姓名、手机号等关键词，或选择工种后点击“查询”</span></div>
      ) : (
        <>
          <div className="table-wrap people-desktop-results">
            <table className="people-combined-table">
              <thead><tr><th>姓名</th><th>工种</th><th>电话</th><th>身份证</th><th>银行卡</th><th>开户行</th><th>薪资标准</th><th>加班单价</th><th>本月天数</th><th>本月加班</th><th>本月薪资</th><th></th></tr></thead>
              <tbody>
                {filteredPeople.length ? filteredPeople.map((person) => {
                  const wage = summaryById.get(person.id);
                  const idKey = `${person.id}:id`;
                  const bankKey = `${person.id}:bank`;
                  return <tr key={person.id}><td><b>{person.name}</b></td><td>{person.trade || "—"}</td><td>{maskId(person.phone)}</td><td><button className="sensitive-value" type="button" disabled={!person.id_no} title="点击显示或隐藏完整身份证号" onClick={() => toggleSensitiveField(idKey)}>{revealedFields.has(idKey) ? person.id_no || "—" : maskId(person.id_no)}</button></td><td><button className="sensitive-value" type="button" disabled={!person.bank_card} title="点击显示或隐藏完整银行卡号" onClick={() => toggleSensitiveField(bankKey)}>{revealedFields.has(bankKey) ? person.bank_card || "—" : maskId(person.bank_card)}</button></td><td>{person.bank_name || "—"}</td><td>{person.salary_type} ¥{person.rate}</td><td>¥{person.overtime_rate}/小时</td><td>{wage ? `${wage.days.toFixed(2)} 天` : "—"}</td><td>{wage ? `${wage.overtime} 小时` : "—"}</td><td>{wage ? <b>¥{wage.payable.toFixed(2)}</b> : "—"}</td><td><button className="text-edit" type="button" onClick={() => beginPersonEdit(person)}>修改</button></td></tr>;
                }) : <tr><td colSpan={12}>当前筛选条件下没有人员</td></tr>}
              </tbody>
            </table>
          </div>
          <div className="people-mobile-results">
            {filteredPeople.length ? filteredPeople.map((person) => {
              const wage = summaryById.get(person.id);
              const idKey = `${person.id}:id`;
              const bankKey = `${person.id}:bank`;
              return <article className="people-mobile-card" key={person.id}>
                <div className="people-mobile-head"><div><b>{person.name}</b><span>{person.trade || "未填工种"}</span></div><button className="text-edit" type="button" onClick={() => beginPersonEdit(person)}>修改</button></div>
                <dl>
                  <div><dt>手机号码</dt><dd>{maskId(person.phone)}</dd></div>
                  <div><dt>身份证号</dt><dd><button className="sensitive-value" type="button" disabled={!person.id_no} onClick={() => toggleSensitiveField(idKey)}>{revealedFields.has(idKey) ? person.id_no || "—" : maskId(person.id_no)}</button></dd></div>
                  <div><dt>银行卡号</dt><dd><button className="sensitive-value" type="button" disabled={!person.bank_card} onClick={() => toggleSensitiveField(bankKey)}>{revealedFields.has(bankKey) ? person.bank_card || "—" : maskId(person.bank_card)}</button></dd></div>
                  <div><dt>开户行</dt><dd>{person.bank_name || "—"}</dd></div>
                  <div><dt>薪资标准</dt><dd>{person.salary_type} ¥{person.rate}</dd></div>
                  <div><dt>加班单价</dt><dd>¥{person.overtime_rate}/小时</dd></div>
                  <div><dt>本月出勤</dt><dd>{wage ? `${wage.days.toFixed(2)}天 · 加班${wage.overtime}小时` : "本月无考勤"}</dd></div>
                  <div className="mobile-wage-total"><dt>本月薪资</dt><dd>{wage ? `¥${wage.payable.toFixed(2)}` : "—"}</dd></div>
                </dl>
              </article>;
            }) : <div className="people-query-empty"><b>没有查询到人员</b><span>请修改关键词或工种后重新查询</span></div>}
          </div>
        </>
      )}
    </section>
  );
}

function ReportsView({
  project,
  date,
  setDate,
  attendance,
  people,
  projectAttendance,
  projectPeople,
  historyLoading,
  notify,
}: {
  project?: Project;
  date: string;
  setDate: (v: string) => void;
  attendance: Attendance[];
  people: Person[];
  projectAttendance: Attendance[];
  projectPeople: Person[];
  historyLoading: boolean;
  notify: (text: string, bad?: boolean) => void;
}) {
  const [checkPerson, setCheckPerson] = useState("");
  const [checkName, setCheckName] = useState("");
  const [checkQueried, setCheckQueried] = useState(false);
  const [reportDetailsVisible, setReportDetailsVisible] = useState(false);
  const [exportingCheck, setExportingCheck] = useState(false);
  const [attendanceExportFormat, setAttendanceExportFormat] = useState<"detail" | "huayue">("detail");
  const [exportingAttendance, setExportingAttendance] = useState(false);
  useEffect(() => {
    setCheckPerson("");
    setCheckName("");
    setCheckQueried(false);
    setReportDetailsVisible(false);
  }, [project?.id, date.slice(0, 7)]);
  // 报表统一以“当月考勤 person_id → 当前项目人员薪资”关联。
  // 使用项目全部人员（包括停用人员），避免顶部合计和个人明细口径不一致。
  const reportPeople = projectPeople.length ? projectPeople : people;
  const currentWage = calculateWageDetails(attendance, reportPeople);
  const reportSummary = currentWage.rows;
  const missingRateNames = reportSummary.filter(row => Number(row.rate || 0) <= 0).map(row => row.name);
  const exportAttendance = async () => {
    if (!project || !attendance.length) return notify("当前项目本月没有可导出的考勤", true);
    setExportingAttendance(true);
    try {
      if (attendanceExportFormat === "huayue") {
        await downloadHuayueAttendanceXlsx(project.name, date.slice(0, 7), attendance, people);
        notify("华粤现场考勤表已生成");
      } else {
        const lines = [
          ["项目", "姓名", "工种", "日期", "区域", "正班小时", "加班小时"],
          ...attendance.map((a) => [
            project.name,
            people.find((p) => p.id === a.person_id)?.name || "",
            people.find((p) => p.id === a.person_id)?.trade || "",
            a.work_date,
            a.area,
            a.regular_hours,
            a.overtime_hours,
          ]),
        ];
        downloadCsv(`考勤明细_${date.slice(0, 7)}.csv`, lines);
      }
    } catch (error) {
      notify(errText(error), true);
    } finally {
      setExportingAttendance(false);
    }
  };
  const exportWage = () => {
    const lines = [
      [
        "项目",
        "姓名",
        "薪资类型",
        "单价",
        "出勤天数",
        "加班小时",
        "加班单价",
        "预计应发",
      ],
      ...reportSummary.map((s) => [
        project?.name || "",
        s.name,
        s.salary_type,
        s.rate,
        s.days,
        s.overtime,
        s.overtime_rate,
        s.payable.toFixed(2),
      ]),
    ];
    downloadCsv(`工资汇总_${date.slice(0, 7)}.csv`, lines);
  };
  const total = currentWage.total;
  const expenseMonths = calculateExpenseByMonth(
    projectAttendance,
    projectPeople,
  );
  const projectTotal = calculateProjectExpense(
    projectAttendance,
    projectPeople,
  );
  const reportParticipantCount = new Set(attendance.map((row) => row.person_id))
    .size;
  const participants = people
    .filter((person) => attendance.some((row) => row.person_id === person.id))
    .sort((a, b) => a.name.localeCompare(b.name, "zh-CN"));
  const checkRows = !checkQueried
    ? []
    : checkPerson
      ? attendance.filter((row) => row.person_id === checkPerson)
      : attendance;
  const checkIssues = checkQueried
    ? attendanceCheckProblems(attendance, people, checkPerson)
    : [];
  const checkRegular = checkRows.reduce(
    (sum, row) => sum + Number(row.regular_hours),
    0,
  );
  const checkOvertime = checkRows.reduce(
    (sum, row) => sum + Number(row.overtime_hours),
    0,
  );
  const searchAttendanceCheck = () => {
    const query = checkName.replace(/\s/g, "").trim();
    if (!query) {
      setCheckPerson("");
      setCheckQueried(true);
      return;
    }
    const exact = participants.find((person) => person.name.replace(/\s/g, "") === query);
    const matches = exact ? [exact] : participants.filter((person) => person.name.replace(/\s/g, "").includes(query));
    if (!matches.length) return notify(`未找到姓名包含“${checkName.trim()}”的本月参勤人员`, true);
    if (matches.length > 1) return notify(`匹配到 ${matches.length} 人，请继续输入更完整的姓名`, true);
    setCheckName(matches[0].name);
    setCheckPerson(matches[0].id);
    setCheckQueried(true);
  };
  const clearAttendanceCheck = () => {
    setCheckName("");
    setCheckPerson("");
    setCheckQueried(false);
  };
  const exportCheck = async () => {
    if (!project || !attendance.length)
      return notify("当前项目本月没有可导出的考勤", true);
    setExportingCheck(true);
    try {
      const count = await exportAttendanceCheckXlsx(
        project.name,
        date.slice(0, 7),
        attendance,
        people,
        checkPerson,
      );
      notify(`已生成考勤核对表，共 ${count} 条明细`);
    } catch (error) {
      notify(errText(error), true);
    } finally {
      setExportingCheck(false);
    }
  };
  return (
    <>
      <div className="stats report-overview-stats">
        <Stat label="本月工资支出" value={total.toFixed(2)} unit="元" />
        <Stat label="项目累计总支出" value={historyLoading ? "统计中" : projectTotal.toFixed(2)} unit={historyLoading ? "" : "元"} />
        <Stat label="本月参勤人员" value={reportParticipantCount} unit="人" />
        <Stat label="累计月份" value={historyLoading ? "统计中" : expenseMonths.length} unit={historyLoading ? "" : "个月"} />
      </div>
      <div className="report-bar">
        <label>
          报表月份
          <input
            type="month"
            value={date.slice(0, 7)}
            onChange={(e) => setDate(`${e.target.value}-01`)}
          />
        </label>
        <div className="actions">
          <label>
            考勤表样式
            <select value={attendanceExportFormat} onChange={(event) => setAttendanceExportFormat(event.target.value as "detail" | "huayue")}> 
              <option value="detail">考勤明细</option>
              <option value="huayue">华粤现场考勤表</option>
            </select>
          </label>
          <button className="primary report-toggle" onClick={() => setReportDetailsVisible((visible) => !visible)}>
            {reportDetailsVisible ? "收起报表明细" : "查询报表明细"}
          </button>
          <button className="secondary" onClick={exportAttendance} disabled={exportingAttendance}>
            {exportingAttendance ? "正在生成" : "导出考勤表"}
          </button>
          <button className="primary" onClick={exportWage}>
            导出工资汇总
          </button>
        </div>
      </div>
      <p className="muted report-expense-note">
        支出按当前项目实际参勤人员的云端考勤和该项目人员薪资标准逐人计算；日薪=出勤天数×日薪，加班=加班小时×加班单价。
      </p>
      {(currentWage.unmatchedPersonIds.length > 0 || missingRateNames.length > 0) && <div className="check-problems" style={{ marginBottom: 17 }}>
        <b>工资统计不完整：</b>
        {currentWage.unmatchedPersonIds.length > 0 && <span>{currentWage.unmatchedPersonIds.length} 名参勤人员无法关联到当前项目人员档案，已暂不计入工资。</span>}
        {missingRateNames.length > 0 && <span>以下参勤人员的薪资单价为0：{missingRateNames.join("、")}。请到“人员薪资”补全。</span>}
      </div>}
      <section className="card expense-history" style={{ marginBottom: 17 }}>
        <div className="card-head">
          <div>
            <h2>项目各月工资支出</h2>
            <p>自动汇总当前项目每个月有考勤人员的工资</p>
          </div>
        </div>
        {reportDetailsVisible ? <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>月份</th>
                <th>参勤人数</th>
                <th>工资支出</th>
              </tr>
            </thead>
            <tbody>
              {expenseMonths
                .slice()
                .reverse()
                .map((item) => (
                  <tr key={item.month}>
                    <td>{item.month}</td>
                    <td>{item.participantCount} 人</td>
                    <td>¥{item.amount.toFixed(2)}</td>
                  </tr>
                ))}
              {!historyLoading && expenseMonths.length === 0 && (
                <tr>
                  <td colSpan={3}>当前项目暂无可汇总的考勤支出</td>
                </tr>
              )}
              {historyLoading && (
                <tr>
                  <td colSpan={3}>正在汇总项目全部考勤…</td>
                </tr>
              )}
            </tbody>
          </table>
        </div> : <div className="people-query-empty"><b>支出明细默认隐藏</b><span>选择报表月份并点击“查询报表明细”后展示</span></div>}
      </section>
      <section className="card attendance-check">
        <div className="card-head">
          <div>
            <h2>考勤核对</h2>
            <p>按桌面端格式生成 Excel；全部人员导出时每人一个工作表</p>
          </div>
          <span className={`pill ${checkQueried && checkIssues.length ? "pill-bad" : ""}`}>
            {!checkQueried ? "等待查询" : checkIssues.length ? `${checkIssues.length} 个问题` : "核对通过"}
          </span>
        </div>
        <div className="card-body check-form">
          <label>
            核对人员
            <input list="attendance-check-person-options" value={checkName} onChange={(event) => { setCheckName(event.target.value); setCheckQueried(false); }} onKeyDown={(event) => { if (event.key === "Enter") searchAttendanceCheck(); }} placeholder="输入姓名；留空查询全部人员" />
            <datalist id="attendance-check-person-options">{participants.map((person) => <option key={person.id} value={person.name} />)}</datalist>
          </label>
          <div className="check-query-actions"><button className="primary" type="button" onClick={searchAttendanceCheck}>查询核对</button><button className="secondary" type="button" onClick={clearAttendanceCheck}>清除</button></div>
          {checkQueried && <div className="check-summary">
            <span>
              明细 <b>{checkRows.length}</b> 条
            </span>
            <span>
              考勤 <b>{(checkRegular / 8).toFixed(2)}</b> 天
            </span>
            <span>
              正班 <b>{checkRegular}</b> 小时
            </span>
            <span>
              加班 <b>{checkOvertime}</b> 小时
            </span>
          </div>}
          {checkQueried && <button
            className="primary"
            disabled={!checkRows.length || exportingCheck}
            onClick={() => void exportCheck()}
          >
            {exportingCheck ? "正在生成…" : "生成考勤核对表（Excel）"}
          </button>}
          {checkQueried && checkIssues.length > 0 && (
            <div className="check-problems">
              <b>导出前请先处理：</b>
              {checkIssues.slice(0, 10).map((problem, index) => (
                <span key={index}>{problem}</span>
              ))}
            </div>
          )}
        </div>
        {checkQueried ? <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>日期</th>
                <th>姓名</th>
                <th>考勤数量</th>
                <th>正班小时</th>
                <th>加班小时</th>
                <th>所在区域</th>
              </tr>
            </thead>
            <tbody>
              {checkRows
                .slice()
                .sort((a, b) => a.work_date.localeCompare(b.work_date))
                .map((row) => (
                  <tr key={row.id}>
                    <td>{row.work_date}</td>
                    <td>
                      {people.find((person) => person.id === row.person_id)
                        ?.name || "未建档人员"}
                    </td>
                    <td>{(Number(row.regular_hours) / 8).toFixed(2)}</td>
                    <td>{row.regular_hours}</td>
                    <td>{row.overtime_hours}</td>
                    <td>{row.area || "未分区域考勤"}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div> : <div className="people-query-empty"><b>考勤核对明细默认隐藏</b><span>输入姓名后查询；姓名留空可查询全部参勤人员</span></div>}
      </section>
      <section className="card">
        <div className="card-head">
          <div>
            <h2>{date.slice(0, 7)} 月度工资测算</h2>
            <p>只显示当前项目本月有考勤记录的人员</p>
          </div>
        </div>
        {reportDetailsVisible ? <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>姓名</th>
                <th>薪资类型</th>
                <th>薪资标准</th>
                <th>出勤天数</th>
                <th>正班小时</th>
                <th>正班工资</th>
                <th>加班小时</th>
                <th>加班工资</th>
                <th>预计应发</th>
              </tr>
            </thead>
            <tbody>
              {reportSummary.map((s) => (
                <tr key={s.id}>
                  <td>
                    <b>{s.name}</b>
                  </td>
                  <td>{s.salary_type}</td>
                  <td>¥{s.rate}</td>
                  <td>{s.days.toFixed(2)}</td>
                  <td>{s.regular}</td>
                  <td>¥{s.basePay.toFixed(2)}</td>
                  <td>{s.overtime}</td>
                  <td>¥{s.overtimePay.toFixed(2)}</td>
                  <td>
                    <strong>¥{s.payable.toFixed(2)}</strong>
                  </td>
                </tr>
              ))}
              {reportSummary.length === 0 && <tr><td colSpan={10}>当前项目本月没有可关联薪资的参勤人员</td></tr>}
            </tbody>
          </table>
        </div> : <div className="people-query-empty"><b>月度工资明细默认隐藏</b><span>选择报表月份并点击“查询报表明细”后展示</span></div>}
      </section>
    </>
  );
}

function ProjectsView({
  ownerId,
  projects,
  current,
  select,
  reload,
  notify,
}: {
  ownerId: string;
  projects: Project[];
  current: string;
  select: (v: string) => void;
  reload: () => Promise<void>;
  notify: (text: string, bad?: boolean) => void;
}) {
  const [name, setName] = useState("");
  const [company, setCompany] = useState("");
  const [deleting, setDeleting] = useState("");
  const [backingUp, setBackingUp] = useState("");
  const [editing, setEditing] = useState("");
  const [editName, setEditName] = useState("");
  const [editCompany, setEditCompany] = useState("");
  const [auditLogs, setAuditLogs] = useState<Record<string, unknown>[]>([]);
  const [auditAvailable, setAuditAvailable] = useState(true);
  const [auditOpen, setAuditOpen] = useState(false);
  const [auditType, setAuditType] = useState("all");
  const [downloadingAudit, setDownloadingAudit] = useState(false);
  const [queueRevision, setQueueRevision] = useState(0);
  const pendingQueue = useMemo(
    () => queuedAttendance(ownerId),
    [ownerId, queueRevision],
  );
  const selectedProject = projects.find((project) => project.id === current);
  useEffect(
    () => onAttendanceQueueChange(() => setQueueRevision((value) => value + 1)),
    [],
  );
  useEffect(() => {
    if (!current) {
      setAuditLogs([]);
      return;
    }
    let cancelled = false;
    const loadAudit = async () => {
      const { data, error } = await supabase
        .from("audit_logs")
        .select("*")
        .eq("project_id", current)
        .order("changed_at", { ascending: false })
        .limit(500);
      if (cancelled) return;
      if (error) {
        setAuditAvailable(false);
        setAuditLogs([]);
      } else {
        setAuditAvailable(true);
        setAuditLogs((data || []) as Record<string, unknown>[]);
      }
    };
    void loadAudit();
    return () => {
      cancelled = true;
    };
  }, [current, projects]);

  const fetchProjectRows = async (table: string, projectId: string) => {
    const rows: Record<string, unknown>[] = [];
    const pageSize = 1000;
    for (let from = 0; ; from += pageSize) {
      const result = await supabase
        .from(table)
        .select("*")
        .eq("project_id", projectId)
        .range(from, from + pageSize - 1);
      if (result.error) throw result.error;
      const page = (result.data || []) as Record<string, unknown>[];
      rows.push(...page);
      if (page.length < pageSize) return rows;
    }
  };
  const downloadBackup = async (project: Project) => {
    if (
      !window.confirm(
        `备份“${project.name}”的全部人员、身份证、银行卡、考勤和工资数据？\n\n备份文件包含敏感信息，请妥善保管。`,
      )
    )
      return;
    setBackingUp(project.id);
    try {
      const [peopleRows, attendanceRows, wageRows] = await Promise.all([
        fetchProjectRows("people", project.id),
        fetchProjectRows("attendance", project.id),
        fetchProjectRows("wage_adjustments", project.id),
      ]);
      let auditRows: Record<string, unknown>[] = [];
      try {
        auditRows = await fetchProjectRows("audit_logs", project.id);
      } catch {
        // 兼容尚未执行审计迁移的数据库，主体备份仍可正常下载。
      }
      const createdAt = new Date();
      downloadJson(
        `工时记_${safeFilename(project.name)}_云端备份_${fileTimestamp(createdAt)}.json`,
        {
          format: "gongshiji-cloud-backup",
          format_version: 1,
          created_at: createdAt.toISOString(),
          app_version: APP_VERSION,
          project,
          people: peopleRows,
          attendance: attendanceRows,
          wage_adjustments: wageRows,
          audit_logs: auditRows,
        },
      );
      notify(
        `备份完成：${peopleRows.length} 人、${attendanceRows.length} 条考勤、${wageRows.length} 条工资调整`,
      );
    } catch (error) {
      notify(`备份失败：${errText(error)}`, true);
    } finally {
      setBackingUp("");
    }
  };
  const downloadAuditLog = async (project: Project) => {
    setDownloadingAudit(true);
    try {
      const rows = await fetchProjectRows("audit_logs", project.id);
      rows.sort((a, b) => String(b.changed_at || "").localeCompare(String(a.changed_at || "")));
      const headers = ["时间", "来源", "操作", "数据类型", "修改内容"];
      const csvRows = rows.map((log) => [
        formatAuditTime(String(log.changed_at || "")),
        auditSourceLabel(String(log.source || "")),
        auditActionLabel(String(log.action || "")),
        auditEntityLabel(String(log.entity_type || "")),
        auditContent(log, false),
      ]);
      downloadCsv(
        `工时记_${safeFilename(project.name)}_操作日志_${fileTimestamp(new Date())}.csv`,
        [headers, ...csvRows],
      );
      notify(`已下载 ${rows.length} 条修改日志；新增和空白补全不在日志中`);
    } catch (error) {
      notify(`日志下载失败：${errText(error)}`, true);
    } finally {
      setDownloadingAudit(false);
    }
  };
  const visibleAuditLogs = auditLogs.filter(
    (log) => auditType === "all" || String(log.entity_type || "") === auditType,
  );
  const save = async (e: FormEvent) => {
    e.preventDefault();
    const { data, error } = await supabase
      .from("projects")
      .insert({
        owner_id: ownerId,
        name: name.trim(),
        company_name: company.trim(),
      })
      .select()
      .single();
    if (error) notify(errText(error), true);
    else {
      setName("");
      setCompany("");
      notify("项目已创建");
      await reload();
      select(data.id);
    }
  };
  const remove = async (project: Project) => {
    const confirmed = window.confirm(
      `确定删除项目“${project.name}”吗？\n\n该项目下的人员、考勤和工资调整数据也会一并删除，此操作不能撤销。`,
    );
    if (!confirmed) return;
    setDeleting(project.id);
    const { error } = await supabase
      .from("projects")
      .delete()
      .eq("id", project.id);
    setDeleting("");
    if (error) return notify(errText(error), true);
    const next = projects.find((item) => item.id !== project.id)?.id || "";
    if (current === project.id) select(next);
    await reload();
    notify(`项目“${project.name}”已删除`);
  };
  const beginProjectEdit = (project: Project) => {
    setEditing(project.id);
    setEditName(project.name);
    setEditCompany(project.company_name || "");
  };
  const saveProjectEdit = async (project: Project) => {
    const nextName = editName.trim();
    if (!nextName) return notify("项目名称不能为空", true);
    const { error } = await supabase
      .from("projects")
      .update({ name: nextName, company_name: editCompany.trim() })
      .eq("id", project.id)
      .eq("owner_id", ownerId);
    if (error) return notify(errText(error), true);
    setEditing("");
    await reload();
    notify(`项目“${nextName}”已更新`);
  };
  return (
    <section className="card">
      <div className="card-head">
        <div>
          <h2>项目管理</h2>
          <p>项目名称只由这里维护，不从粘贴文字中自动修改</p>
        </div>
      </div>
      <div className="card-body">
        <form className="project-form" onSubmit={save}>
          <label>
            项目名称
            <input
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="凝结水回收项目"
            />
          </label>
          <label>
            单位 / 公司名称
            <input
              value={company}
              onChange={(e) => setCompany(e.target.value)}
              placeholder="工资表抬头"
            />
          </label>
          <button className="primary">创建项目</button>
        </form>
        <div className="project-grid">
          {projects.map((p) => (
            <div
              className={`project-item ${p.id === current ? "selected" : ""}`}
              key={p.id}
            >
              {editing === p.id ? (
                <div className="project-edit-form">
                  <label>项目名称<input value={editName} onChange={(event) => setEditName(event.target.value)} /></label>
                  <label>单位 / 公司名称<input value={editCompany} onChange={(event) => setEditCompany(event.target.value)} /></label>
                  <div className="actions"><button className="secondary" type="button" onClick={() => setEditing("")}>取消</button><button className="primary" type="button" onClick={() => void saveProjectEdit(p)}>保存修改</button></div>
                </div>
              ) : (
                <>
                  <button className="project-open" onClick={() => select(p.id)}><b>{p.name}</b><span>{p.company_name || "未设置公司名称"}</span></button>
                  <button className="project-edit" type="button" onClick={() => beginProjectEdit(p)}>修改</button>
                  <button className="project-backup" disabled={backingUp === p.id} onClick={() => void downloadBackup(p)}>{backingUp === p.id ? "备份中…" : "下载备份"}</button>
                  <button className="project-delete" disabled={deleting === p.id} onClick={() => void remove(p)}>{deleting === p.id ? "删除中…" : "删除"}</button>
                </>
              )}
            </div>
          ))}
        </div>
        <div className="data-safety">
          <div>
            <h3>数据安全</h3>
            <p>建议每周下载一次完整备份；文件包含身份证和银行卡等敏感资料，请保存在可信设备。</p>
          </div>
          {selectedProject && (
            <button
              type="button"
              className="primary safety-backup-button"
              disabled={Boolean(backingUp)}
              onClick={() => void downloadBackup(selectedProject)}
            >
              {backingUp === selectedProject.id
                ? "正在生成备份…"
                : "下载当前项目完整备份"}
            </button>
          )}
          <span className={`pill ${pendingQueue.length ? "pill-bad" : ""}`}>
            本机待同步 {pendingQueue.length} 批
          </span>
          {pendingQueue.length > 0 && (
            <button
              className="danger"
              onClick={() => {
                if (!window.confirm("确定清除本机全部待同步考勤吗？清除后不能恢复。")) return;
                clearQueuedAttendance(ownerId);
                notify("本机待同步记录已清除");
              }}
            >
              清除待同步
            </button>
          )}
        </div>
        <div className="audit-panel">
          <div className="card-head">
            <div>
              <h2>操作日志</h2>
              <p>仅记录非空数据被覆盖和删除；新增、自动建档及空白资料补全不记录，日志自动保留 90 天</p>
            </div>
            <div className="actions audit-actions">
              <button className="secondary" type="button" onClick={() => setAuditOpen((value) => !value)}>
                {auditOpen ? "收起日志" : "查看日志"}
              </button>
              {selectedProject && (
                <button className="secondary" type="button" disabled={downloadingAudit} onClick={() => void downloadAuditLog(selectedProject)}>
                  {downloadingAudit ? "正在下载…" : "下载日志"}
                </button>
              )}
            </div>
          </div>
          {!auditAvailable ? (
            <div className="audit-not-ready">审计数据库尚未升级，请执行最新的审计日志数据库迁移。</div>
          ) : auditOpen && auditLogs.length === 0 ? (
            <Empty text="当前项目还没有需要记录的修改或删除操作" />
          ) : auditOpen ? (
            <>
              <div className="audit-toolbar">
                <label>数据类型
                  <select value={auditType} onChange={(event) => setAuditType(event.target.value)}>
                    <option value="all">全部</option>
                    <option value="project">项目</option>
                    <option value="person">人员</option>
                    <option value="attendance">考勤</option>
                    <option value="wage_adjustment">工资调整</option>
                  </select>
                </label>
                <span>当前显示 {visibleAuditLogs.length} 条，最多加载最近 500 条</span>
              </div>
              <div className="table-wrap">
              <table>
                <thead><tr><th>时间</th><th>来源</th><th>操作</th><th>数据类型</th><th>修改内容</th></tr></thead>
                <tbody>
                  {visibleAuditLogs.map((log) => (
                    <tr key={String(log.id)}>
                      <td data-label="时间">{formatAuditTime(String(log.changed_at || ""))}</td>
                      <td data-label="来源">{auditSourceLabel(String(log.source || ""))}</td>
                      <td data-label="操作">{auditActionLabel(String(log.action || ""))}</td>
                      <td data-label="数据类型">{auditEntityLabel(String(log.entity_type || ""))}</td>
                      <td data-label="修改内容">{auditContent(log)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            </>
          ) : (
            <div className="audit-collapsed">点击“查看日志”审查当前项目的修改和删除记录。</div>
          )}
        </div>
      </div>
    </section>
  );
}

function Stat({
  label,
  value,
  unit,
}: {
  label: string;
  value: number | string;
  unit: string;
}) {
  return (
    <div className="stat">
      <span>{label}</span>
      <strong>
        {value}
        <small>{unit}</small>
      </strong>
    </div>
  );
}
function Empty({ text }: { text: string }) {
  return <div className="empty">{text}</div>;
}
function downloadCsv(name: string, rows: unknown[][]) {
  const csv =
    "\ufeff" +
    rows
      .map((row) =>
        row.map((v) => `"${String(v ?? "").replaceAll('"', '""')}"`).join(","),
      )
      .join("\r\n");
  const url = URL.createObjectURL(
    new Blob([csv], { type: "text/csv;charset=utf-8" }),
  );
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
}

function downloadJson(name: string, value: unknown) {
  const url = URL.createObjectURL(
    new Blob([JSON.stringify(value, null, 2)], {
      type: "application/json;charset=utf-8",
    }),
  );
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = name;
  anchor.style.display = "none";
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 5000);
}

function safeFilename(value: string) {
  return value.replace(/[\\/:*?"<>|]/g, "_").trim() || "未命名项目";
}

function fileTimestamp(value: Date) {
  const pad = (part: number) => String(part).padStart(2, "0");
  return `${value.getFullYear()}${pad(value.getMonth() + 1)}${pad(value.getDate())}_${pad(value.getHours())}${pad(value.getMinutes())}${pad(value.getSeconds())}`;
}

function auditActionLabel(value: string) {
  return ({ insert: "新增", update: "修改", delete: "删除" } as Record<string, string>)[value] || value;
}

function auditEntityLabel(value: string) {
  return (
    {
      project: "项目",
      person: "人员",
      attendance: "考勤",
      wage_adjustment: "工资调整",
    } as Record<string, string>
  )[value] || value;
}

function auditSourceLabel(value: string) {
  return (
    {
      desktop_sync: "桌面端同步",
      web_api: "网页AI/接口",
      web_or_database: "网页端/数据库",
    } as Record<string, string>
  )[value] || "网页端/数据库";
}

function formatAuditTime(value: string) {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString("zh-CN", { hour12: false });
}

const auditFieldLabels: Record<string, string> = {
  name: "姓名", company_name: "单位", trade: "工种", phone: "手机号",
  id_no: "身份证号", bank_card: "银行卡号", bank_name: "开户行",
  salary_type: "薪资类型", rate: "薪资单价", overtime_rate: "加班单价",
  active: "启用状态", work_date: "日期", area: "区域", regular_hours: "正班小时",
  overtime_hours: "加班小时", remark: "备注", bonus: "奖金", deduction: "扣款", tax: "个税",
};

function auditValue(field: string, value: unknown, maskSensitive: boolean) {
  if (value === null || value === undefined || value === "") return "空";
  const text = typeof value === "object" ? JSON.stringify(value) : String(value);
  if (maskSensitive && ["phone", "id_no", "bank_card"].includes(field) && text.length > 7)
    return `${text.slice(0, 3)}***${text.slice(-4)}`;
  if (text === "true") return "启用";
  if (text === "false") return "停用";
  return text.length > 80 ? `${text.slice(0, 80)}…` : text;
}

function auditContent(log: Record<string, unknown>, maskSensitive = true) {
  const oldData = (log.old_data || {}) as Record<string, unknown>;
  const newData = (log.new_data || {}) as Record<string, unknown>;
  const subject = String(log.subject_name || newData.name || oldData.name || "").trim();
  if (String(log.action || "") === "delete") {
    const details = [oldData.work_date, oldData.area].map((value) => String(value || "").trim()).filter(Boolean);
    return [subject, ...details, "已删除"].filter(Boolean).join(" · ") || "记录已删除";
  }
  const ignored = new Set(["id", "owner_id", "project_id", "person_id", "created_at", "updated_at"]);
  const changes = Array.from(new Set([...Object.keys(oldData), ...Object.keys(newData)]))
    .filter((field) => !ignored.has(field) && JSON.stringify(oldData[field]) !== JSON.stringify(newData[field]))
    .map((field) => `${auditFieldLabels[field] || field}：${auditValue(field, oldData[field], maskSensitive)} → ${auditValue(field, newData[field], maskSensitive)}`);
  return [subject, ...changes].filter(Boolean).join("｜") || String(log.entity_id || "—");
}
